// Greek copy for the simulator. Kept apart from the engine so the rules and the
// words describing them can be edited without touching each other.
import { ORDER_SKIP_REASONS, SKIP_REASONS } from './autoAssignSimulation.js';

// Why this driver did not get this order. Written to name the setting
// responsible wherever there is one, because the next thing the admin will do
// is go and change it.
export const skipReasonText = (reason, { settings } = {}) => {
	switch (reason) {
		case SKIP_REASONS.BLOCKED:
			return 'Είναι μπλοκαρισμένος';
		case SKIP_REASONS.NO_PUSH_TOKEN:
			return 'Δεν έχει ενεργές ειδοποιήσεις στο app';
		case SKIP_REASONS.EXCLUDED_FROM_AUTO_ASSIGN:
			return 'Είναι στους διανομείς που εξαιρούνται από τον αυτόματο έλεγχο';
		case SKIP_REASONS.ON_PIECE_WORK:
			return 'Έχει ανοιχτή εργασία φακέλων / εφημερίδων';
		case SKIP_REASONS.ALREADY_IGNORED:
			return 'Του δόθηκε ήδη αυτή η παραγγελία και δεν απάντησε';
		case SKIP_REASONS.REMOVED_BY_ADMIN:
			return 'Του αφαιρέθηκε αυτή η παραγγελία από υπεύθυνο';
		// Fires only BELOW the limit — the count on the card would otherwise make
		// this look like the board is wrong.
		case SKIP_REASONS.HOLDING_UNTIL_EMPTY:
			return 'Έπιασε το όριο παραγγελιών και δεν τις έχει ολοκληρώσει όλες ακόμα';
		// The limits below are city columns, not part of the scoring blob, so a
		// decision stored before they were recorded has no number to show. Saying
		// «0 ή περισσότερες» in that case reads as "everyone is always over the
		// limit", which is worse than not naming it — hence the plain form.
		case SKIP_REASONS.TOO_MANY_ORDERS: {
			const limit = Number(settings?.orderAutoAssignMinFreeOrders) || 0;
			return limit > 0
				? `Έχει ήδη ${limit} ή περισσότερες ενεργές παραγγελίες`
				: 'Έχει ήδη όσες ενεργές παραγγελίες επιτρέπει το όριο';
		}
		case SKIP_REASONS.TOO_SOON: {
			const seconds = Number(settings?.orderAutoAssignMinFreeMinutes) || 0;
			return seconds > 0
				? `Ολοκλήρωσε παραγγελία πριν από λιγότερο από ${seconds} δευτερόλεπτα`
				: 'Ολοκλήρωσε παραγγελία πιο πρόσφατα από το όριο αναμονής';
		}
		case SKIP_REASONS.NOT_CLOCKED_IN:
			return 'Δεν είναι σε βάρδια (ωρομέτρηση)';
		case SKIP_REASONS.NOT_ALLOWED_BY_SHOP:
			return 'Το κατάστημα δεν τον έχει στους επιτρεπόμενους διανομείς';
		// Had no text at all until now: the ceiling is a newer setting and the
		// settings page's worked example writes its own message, so the gap only
		// showed on the live decision board — as an empty red line.
		case SKIP_REASONS.TOO_FAR:
			return settings?.autoAssignScoring?.maxDistanceMeters
				? `Είναι πιο μακριά από το όριο των ${settings.autoAssignScoring.maxDistanceMeters} μ.`
				: 'Είναι πιο μακριά από το όριο απόστασης';
		case SKIP_REASONS.TAKEN_BY_EARLIER_ORDER:
			return 'Δόθηκε σε παλαιότερη παραγγελία στο ίδιο πέρασμα';
		// Counts what the rider has IN HAND — orders already on them plus anything
		// this pass just gave them. It used to bound one pass only, and the label
		// said so; the wording outlived the change.
		case SKIP_REASONS.BATCH_FULL: {
			const cap = Number(settings?.autoAssignScoring?.maxBatchSize) || 0;
			return cap > 0
				? `Κρατάει ήδη ${cap} παραγγελίες, όσες επιτρέπονται ταυτόχρονα`
				: 'Κρατάει ήδη όσες παραγγελίες επιτρέπονται ταυτόχρονα';
		}
		case SKIP_REASONS.NO_BATCH_TO_JOIN:
			return 'Δεν έχει διαδρομή στην οποία να μπει αυτή η παραγγελία';
		case SKIP_REASONS.SCORE_TOO_LOW:
			return 'Ο βαθμός του είναι κάτω από το ελάχιστο για ανάθεση';
		default:
			return '';
	}
};

export const orderSkipReasonText = reason => {
	switch (reason) {
		case ORDER_SKIP_REASONS.NOT_OPERATING:
			return 'Η πόλη είναι σε παύση εργασιών — η αυτόματη ανάθεση κάθε 30δ δεν τρέχει καθόλου';
		// Also had no text. Rarer than the others — the sweep only reports it when
		// an order was claimed between reading the list and writing — but a
		// timeline row that explains nothing is exactly what the timeline is for.
		case ORDER_SKIP_REASONS.ALREADY_ASSIGNED:
			return 'Είχε ήδη διανομέα όταν έφτασε ο έλεγχος σε αυτήν';
		case ORDER_SKIP_REASONS.NOT_READY:
			return 'Δεν είναι ακόμα έτοιμη';
		case ORDER_SKIP_REASONS.NOT_PENDING_LONG_ENOUGH:
			return 'Δεν είναι έτοιμη για αρκετή ώρα';
		case ORDER_SKIP_REASONS.NO_FREE_DRIVER:
			return 'Κανένας διαθέσιμος διανομέας';
		default:
			return '';
	}
};

export const triggerText = trigger => {
	switch (trigger) {
		// The simulator never produces these two any more — the per-order triggers
		// are gone. Kept because assignment_decisions rows written before that
		// still carry them.
		case 'creation':
			return 'Κατά την καταχώρηση (παλιό)';
		case 'ready':
			return 'Μόλις ετοιμάστηκε (παλιό)';
		case 'sweep':
			return 'Έλεγχος κάθε 30δ';
		case 'lookahead':
			return 'Μπήκε σε διαδρομή πριν ετοιμαστεί';
		default:
			return '';
	}
};

// mm:ss from the start of the scenario — the timeline is short enough that a
// clock time would be noise.
export const formatSimTime = seconds => {
	const s = Math.max(0, Math.round(seconds));
	const mins = Math.floor(s / 60);
	const secs = s % 60;
	return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

// One colour per criterion, carried through every place it appears: its weight
// slider, the scale that feeds it, and its row in the worked example. Four
// abstract numbers that combine into a fifth are hard to hold in your head; a
// colour lets you trace one criterion across the screen without doing that.
//
// Emerald is deliberately not used — it means "this rider wins" in the example.
export const TERM_COLORS = {
	idle: { dot: 'bg-violet-500', text: 'text-violet-700', accent: '#7c3aed' },
	distance: { dot: 'bg-sky-500', text: 'text-sky-700', accent: '#0284c7' },
	load: { dot: 'bg-amber-500', text: 'text-amber-700', accent: '#d97706' },
	batching: { dot: 'bg-pink-500', text: 'text-pink-700', accent: '#db2777' },
	// Not a fifth criterion — how much a rider's GPS fix is worth believing. It
	// feeds the distance criterion, but it is not a scale OF distance: it is
	// about the quality of what we know, and giving it the distance colour made
	// two very different settings look like the same thing. Deliberately grey,
	// so it reads as a modifier rather than as another thing being scored.
	position: { dot: 'bg-slate-400', text: 'text-slate-600', accent: '#64748b' },
};

export const termColor = key => TERM_COLORS[key] || { dot: 'bg-slate-400', text: 'text-slate-600' };

// Greek thousands separator, so 2500 reads as 2.500 next to a scale of 3.000.
// A string passes through untouched: one chip in the preset table is a yes/no
// and derives its own word, and '—' would hide it rather than say "off".
export const formatNumber = n =>
	typeof n === 'number' && isFinite(n)
		? n.toLocaleString('el-GR')
		: typeof n === 'string' && n
			? n
			: '—';

export const CLOCK_EVENT_OPTIONS = [
	{ value: 'ci', label: 'Σε βάρδια' },
	{ value: 'bo', label: 'Γύρισε από διάλειμμα' },
	{ value: 'bi', label: 'Σε διάλειμμα' },
	{ value: 'co', label: 'Εκτός βάρδιας' },
	{ value: 'none', label: 'Καμία κίνηση' },
];
